import { Button, Flex, Form, Input, Spin, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { UserAddress } from "@/types/schema/xconsTypess";
import { useEffect, useRef, useState } from "react";
import {
  useJsApiLoader,
  Autocomplete,
  GoogleMap,
  MarkerF,
} from "@react-google-maps/api";
import { PhoneNumberInput } from "@/components/phone-number-input";
import xconsMap from "@/assets/img/xcons-map.svg";
import { CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { VITE_GOOGLE_MAPS_API_KEY } from "@/services/XCONS_ENV";

const API_KEY = VITE_GOOGLE_MAPS_API_KEY;

const libraries: "places"[] = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "200px",
  borderRadius: "8px",
  marginBottom: "16px",
  border: "1px solid #d9d9d9",
};

const defaultCenter = {
  lat: -34.603722,
  lng: -58.381592,
};

interface Props {
  address: UserAddress | null;
  visible: boolean;
  onClose: () => void;
  onUpdate: (updatedAddress: UserAddress) => void;
}

export const DeliveryAgenda = ({
  address,
  visible,
  onClose,
  onUpdate,
}: Props) => {
  const { t } = useTranslation();
  const [locationData, setLocationData] = useState<{
    latitude: any;
    longitude: any;
    countryId: string;
  }>({
    latitude: "-34.603722",
    longitude: "-58.381592",
    countryId: "AR",
  });

  const [form] = useForm();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markerLoaded, setMarkerLoaded] = useState(false);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
    libraries,
    language: "es",
    region: "AR",
    version: "weekly",
  });

  useEffect(() => {
    if (address) {
      form.setFieldsValue(address);
      setLocationData({
        latitude: address.latitude || "-34.603722",
        longitude: address.longitude || "-58.381592",
        countryId: address.countryId || "AR",
      });
    }
  }, [address]);

  useEffect(() => {
    if (loadError) {
      setError("Error loading Google Maps API");
      setLoading(false);
    } else if (isLoaded) {
      setLoading(false);
      setError(null);
    }
  }, [isLoaded, loadError]);

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;

    const lat = place.geometry.location.lat().toString();
    const lng = place.geometry.location.lng().toString();

    const addressComponents = place.address_components || [];

    const getComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name;

    const getShortComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.short_name;

    const fullAddress = [getComponent("route"), getComponent("street_number")]
      .filter(Boolean)
      .join(", ");

    form.setFieldsValue({
      street: fullAddress,
      city: getComponent("locality"),
      region: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      postcode: getComponent("postal_code"),
    });

    setLocationData({
      latitude: lat,
      longitude: lng,
      countryId: getShortComponent("country") || "AR",
    });
  };

  const onAutocompleteLoad = (
    autocomplete: google.maps.places.Autocomplete,
  ) => {
    if (!window.google) return;

    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-55.174989, -73.563892),
      new window.google.maps.LatLng(-21.770833, -53.984375),
    );

    autocomplete.setBounds(bounds);
    autocomplete.setComponentRestrictions({ country: ["AR"] });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place && place.address_components) {
        handlePlaceSelect(place);
      }
    });
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const lat = String(locationData.latitude ?? "").trim();
      const lng = String(locationData.longitude ?? "").trim();
      if (!lat || !lng) {
        message.error(
          "Seleccioná la dirección desde el autocompletado para obtener la ubicación en el mapa.",
        );
        return;
      }
      const updatedAddress = {
        ...address,
        ...values,
        latitude: lat,
        longitude: lng,
        countryId: locationData.countryId,
      };
      onUpdate(updatedAddress);
    });
  };

  if (loadError) return <div>{t("contacts.address.error.googlemaps")}</div>;
  if (!isLoaded) return <Spin spinning />;

  return (
    <Form layout="vertical" form={form}>
      <Flex gap={8} justify="space-between">
        <Form.Item
          label="Name"
          name="firstname"
          initialValue={address?.firstname}
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("lastname")}
          name="lastname"
          initialValue={address?.lastname}
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input />
        </Form.Item>
      </Flex>

      <Form.Item
        label="Phone number"
        name="telephone"
        initialValue={address?.telephone}
        rules={[
          {
            required: true,
            message: t("contacts.address.validation.whatsapprequired"),
          },
          {
            validator: (_, value) => {
              const phoneDigits = value?.replace(/\D/g, "");
              if (!phoneDigits || phoneDigits.length < 11) {
                return Promise.reject(
                  t("contacts.address.validation.whatsapplength"),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <PhoneNumberInput country={address?.countryId?.toLowerCase() || "ar"} />
      </Form.Item>

      <Form.Item
        label={t("contacts.label.deliveryinstructions")}
        name="shippingIndication"
      >
        <Input.TextArea
          rows={4}
          placeholder={t("contacts.placeholder.instructionsfordelivery")}
        />
      </Form.Item>

      <Autocomplete onLoad={onAutocompleteLoad}>
        <Form.Item
          label={t("contacts.label.address")}
          name="street"
          rules={[
            {
              required: true,
              message: t("contacts.address.validation.street"),
            },
          ]}
        >
          <Input placeholder={t("contacts.placeholder.addressgooglemaps")} />
        </Form.Item>
      </Autocomplete>

      <div style={{ width: "100%" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{
            lat: parseFloat(locationData.latitude),
            lng: parseFloat(locationData.longitude),
          }}
          zoom={15}
          onLoad={(map) => {
            setMap(map);
            setLoading(false);
            setMarkerLoaded(true);
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
          }}
        >
          <MarkerF
            position={{
              lat: parseFloat(locationData.latitude),
              lng: parseFloat(locationData.longitude),
            }}
            options={{
              animation: window.google.maps.Animation.DROP,
            }}
            icon={{
              url: xconsMap,
              scaledSize: new window.google.maps.Size(45, 45),
            }}
          />
        </GoogleMap>
      </div>

      <Form.Item label="City" name="city" initialValue={address?.city}>
        <Input />
      </Form.Item>

      <Form.Item
        label="Zip code"
        name="postcode"
        initialValue={address?.postcode}
        rules={[{ required: true, message: "Requerido" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="State" name="region" initialValue={address?.region}>
        <Input />
      </Form.Item>

      <Flex justify="end" gap={8}>
        <Button danger onClick={onClose}>
          {t("buttons.cancel")}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          icon={<CheckOutlined />} iconPosition="end"
        >
          {t("buttons.save")}
        </Button>
      </Flex>
    </Form>
  );
};
